import type { Equation } from "@src/features/equation-editor/equation";
import {
  focusOpenedPopupMenu,
  handlePopupMenuItemFocus,
  handlePopupMenuKeyDown,
  handlePopupMenuTriggerKeyDown,
} from "../lib/popup-menu";
import { DiagnosticBadge } from "./cards/DiagnosticField";
import { MAX_EQUATION_NODES } from "@src/features/equation-editor/equation-policy";
import type { Translate } from "@src/i18n/translate";
import {
  analyzeEquationCapabilities,
  type EquationCapabilities,
} from "@src/features/equation-editor/capability-analysis";
import { canAddEquationNodes } from "@src/features/equation-editor/equation-operations";
import { useId } from "react";
import type { Dimension } from "@src/math/types";
import { PresetOption, usePopupMenuRef } from "./PresetMenu";

export function AddVectorControls<D extends Dimension>({
  equation,
  t,
  onAdd,
  onEigenbasis,
  onEigenvector,
  capabilities = analyzeEquationCapabilities(equation),
  inline = false,
}: {
  equation: Equation<D>;
  t: Translate;
  onAdd: () => void;
  onEigenbasis: () => void;
  onEigenvector: () => void;
  capabilities?: EquationCapabilities<Dimension>;
  inline?: boolean;
}) {
  const disabled = !canAddEquationNodes(equation, "vectors");
  const eigenvectorDisabledReason = getEigenvectorDisabledReason(
    capabilities,
    t,
  );
  const eigenbasisDisabledReason = getEigenbasisDisabledReason(capabilities, t);
  const eigenvectorReasonId = useId();
  const eigenbasisReasonId = useId();
  const popupMenuRef = usePopupMenuRef();
  return (
    <div
      className={`preset-split vector-add-button ${inline ? "vector-add-button-inline" : `vector-add-button-${String(equation.dimension)}`}`}
    >
      {disabled && (
        <DiagnosticBadge
          id={`vector-capacity-${String(equation.dimension)}`}
          messages={[t("vectorCapacityReached", { count: MAX_EQUATION_NODES })]}
          indicatorLabel={t("limitDetails")}
        />
      )}
      <button
        className="equation-add-button preset-main"
        type="button"
        onClick={onAdd}
        aria-label={`${t("addVector")} + v`}
        title={t("addVector")}
        disabled={disabled}
      >
        <math aria-hidden="true">
          <mo>+</mo>
          <mi>v</mi>
        </math>
      </button>
      <details
        ref={popupMenuRef}
        className="preset-menu popup-menu"
        onToggle={focusOpenedPopupMenu}
      >
        <summary
          className="preset-toggle"
          aria-label={t("vectorPresets")}
          title={t("vectorPresets")}
          aria-haspopup="menu"
          onKeyDown={handlePopupMenuTriggerKeyDown}
        />
        <div
          className="preset-menu-panel popup-menu-panel"
          role="menu"
          tabIndex={-1}
          aria-label={t("vectorPresets")}
          onKeyDown={handlePopupMenuKeyDown}
          onFocus={handlePopupMenuItemFocus}
        >
          <PresetOption
            label={t("oneEigenvector")}
            available={capabilities.eigenvector.kind === "available"}
            initialTabStop
            disabledReason={eigenvectorDisabledReason}
            reasonId={eigenvectorReasonId}
            onSelect={onEigenvector}
          />
          <PresetOption
            label={t("allEigenbasis")}
            available={capabilities.eigenbasis.kind === "available"}
            initialTabStop={false}
            disabledReason={eigenbasisDisabledReason}
            reasonId={eigenbasisReasonId}
            onSelect={onEigenbasis}
          />
        </div>
      </details>
    </div>
  );
}

function getEigenvectorDisabledReason(
  capabilities: EquationCapabilities<Dimension>,
  t: Translate,
): string | undefined {
  switch (capabilities.eigenvector.kind) {
    case "available":
      return undefined;
    case "invalid-matrices":
      return t("eigenPresetsRequireValidMatrices");
    case "unavailable":
      return t("eigenvectorUnavailable");
    case "capacity-exceeded":
      return t("eigenvectorPresetCapacityReached");
  }
}

function getEigenbasisDisabledReason(
  capabilities: EquationCapabilities<Dimension>,
  t: Translate,
): string | undefined {
  switch (capabilities.eigenbasis.kind) {
    case "available":
      return undefined;
    case "invalid-matrices":
      return t("eigenPresetsRequireValidMatrices");
    case "unavailable":
      return t("eigenbasisUnavailable");
    case "capacity-exceeded":
      return t("eigenbasisPresetCapacityReached");
  }
}
